import { FC, FocusEventHandler, useMemo, useRef, useState } from 'react'

import { styled } from 'styled-components'

import { Tooltip } from 'core/components'
import variables from 'core/styles/variables'

import BorrowerSearchInput, {
  BorrowerSearchInputColonValidator,
  BorrowerSearchInputOptimizer,
  BorrowerSearchInputSubmitHandler,
  BorrowerSearchInputWrapper,
  SearchPart,
} from './BorrowerSearchInput'
import {
  BorrowerSearchInputChangeHandler,
  BorrowerSearchInputClearButton,
} from './BorrowerSearchInputClearButton'
import BorrowerSearchToolbar, {
  BorrowerSearchInputSanitizer,
} from './BorrowerSearchToolbar'
import BorrowerSearchTooltipContent from './BorrowerSearchTooltipContent'

type BorrowerSearchFormProps = {
  isOptimized: boolean
  searchInputValue: string
  searchParts: Array<SearchPart>
  usedPrefixes: Array<string>
  isColonValidAtIndex: BorrowerSearchInputColonValidator
  sanitizeSearchInput: BorrowerSearchInputSanitizer
  onChangeSearchInput: BorrowerSearchInputChangeHandler
  onSubmitSearchInput: BorrowerSearchInputSubmitHandler
  optimizeSearchInput: BorrowerSearchInputOptimizer
}

const BorrowerSearchForm: FC<BorrowerSearchFormProps> = ({
  isOptimized,
  searchInputValue,
  searchParts,
  usedPrefixes,
  isColonValidAtIndex,
  sanitizeSearchInput,
  onChangeSearchInput,
  onSubmitSearchInput,
  optimizeSearchInput,
}) => {
  const [inputHeight, setInputHeight] = useState(40)
  const [isToolbarVisible, setIsToolbarVisible] = useState(false)

  const clearRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)

  const hasInvalidSearchParts = useMemo(
    () => searchParts.some((p) => !p.valid),
    [searchParts],
  )

  const setFocusOnSearchInput = () => {
    if (inputRef.current) inputRef.current.focus()
  }
  const setFocusOnToolbar = () => {
    if (toolbarRef.current) toolbarRef.current.focus()
  }

  const handleBlur: FocusEventHandler<HTMLTextAreaElement | HTMLDivElement> = (
    e,
  ) => {
    if (
      (clearRef.current === null || clearRef.current !== e.relatedTarget) &&
      inputRef.current !== e.relatedTarget &&
      toolbarRef.current !== e.relatedTarget &&
      !toolbarRef.current?.contains(e.relatedTarget)
    ) {
      setIsToolbarVisible(false)
    }
  }

  return (
    <FlexRow>
      <FormWrapper height={`${inputHeight}px`}>
        <Form showShadow={isToolbarVisible}>
          <BorrowerSearchInputWrapper>
            <BorrowerSearchInput
              handleBlur={handleBlur}
              inputHeight={`${inputHeight}px`}
              isColonValidAtIndex={isColonValidAtIndex}
              isOptimized={isOptimized}
              onChangeSearchInput={onChangeSearchInput}
              onSubmitSearchInput={onSubmitSearchInput}
              optimizeSearchInput={optimizeSearchInput}
              ref={inputRef}
              searchInputValue={searchInputValue}
              searchParts={searchParts}
              setFocusOnToolbar={setFocusOnToolbar}
              setInputHeight={setInputHeight}
              setIsToolbarVisible={setIsToolbarVisible}
            />

            {searchInputValue.length > 0 && (
              <BorrowerSearchInputClearButton
                onChangeSearchInput={onChangeSearchInput}
                ref={clearRef}
                setFocusOnSearchInput={setFocusOnSearchInput}
              />
            )}
          </BorrowerSearchInputWrapper>

          {isToolbarVisible && hasInvalidSearchParts && (
            <Banner $variant='error'>
              Your search contains invalid parameters
            </Banner>
          )}

          {isToolbarVisible && (
            <BorrowerSearchToolbar
              handleBlur={handleBlur}
              isOptimized={isOptimized}
              onChangeSearchInput={onChangeSearchInput}
              optimizeSearchInput={optimizeSearchInput}
              ref={toolbarRef}
              sanitizeSearchInput={sanitizeSearchInput}
              searchParts={searchParts}
              setFocusOnSearchInput={setFocusOnSearchInput}
              usedPrefixes={usedPrefixes}
            />
          )}
        </Form>
      </FormWrapper>

      <TippyWrapper>
        <Tooltip
          animation='shift-away'
          backgroundColor={variables.colorBlueLighten}
          content={BorrowerSearchTooltipContent}
          hideOnClick
          iconSize='22px'
          placement='bottom-end'
          width={648}
        />
      </TippyWrapper>
    </FlexRow>
  )
}

export default BorrowerSearchForm

const FormWrapper = styled.div<{ height: string }>`
  position: relative;
  width: 100%;
  height: 100%;
`
const FlexRow = styled.div`
  display: flex;
  flex: 1;
  align-items: flex-start;
`
const Form = styled.form<{ showShadow: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 1;
  border-radius: 8px;
  background: ${(p) => p.theme.background};
  width: 100%;
  ${(p) => p.showShadow && `box-shadow: ${variables.peachyShadow};`};
`
const TippyWrapper = styled.div`
  margin: 9px 0 0 8px;
  cursor: pointer;
`

type BannerProps = {
  /** The visual style of the banner. */
  $variant: 'error' | 'success' | 'warning' | 'ghost'
}

const Banner = styled.div<BannerProps>`
  display: flex;
  grid-column: 1 / -1;
  align-items: center;
  background-color: ${(p) =>
    p.$variant === 'ghost'
      ? 'transparent'
      : p.$variant === 'error'
      ? variables.colorRedLighten
      : p.$variant === 'warning'
      ? variables.colorOrangeLighten
      : variables.colorGreenLighten};
  padding: 4px 16px;
  padding-top: 12px;
  padding-bottom: 12px;
  color: ${(p) => p.theme.text};
  font-size: 14px;

  &:last-child {
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
  }
`
